import React, { useState } from 'react'
import { Pencil, AlertCircle } from 'lucide-react'
import { EditCampaignModal } from '@/components/campaign/EditCampaignModal'

interface CampaignDetailsCollapsibleProps {
  campaign: {
    id?: string
    name: string
    startDate: string
    endDate: string
    budget: number | null
    socialPlatform?: string[]
    heroImage?: string
    logoUrl?: string
    targeting: {
      sports: string[]
      gender: string[]
      geography: {
        states: string[]
        regions: string[]
        schools: string[]
      }
      interests: string[]
    }
    creative?: Record<string, unknown>
    campaignBrief?: {
      description?: string
      objectives?: string[]
      primaryKpi?: string
      targetAgeRange?: string
      targetGender?: string
      targetLocation?: string
      targetAudienceDescription?: string
      primaryMessage?: string
      supportingPoints?: string[]
      cta?: string
      tones?: string[]
      creativeDirection?: string
      additionalNotes?: string
    } | null
  }
  getCreativeName: (type: string, id: string) => string
}

function isBriefIncomplete(
  brief: CampaignDetailsCollapsibleProps['campaign']['campaignBrief'],
): boolean {
  if (!brief) return true
  return (
    !brief.primaryMessage?.trim() || !brief.creativeDirection?.trim() || !brief.objectives?.length
  )
}

export const CampaignDetailsCollapsible: React.FC<CampaignDetailsCollapsibleProps> = ({
  campaign,
  getCreativeName,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const briefNeedsAttention = isBriefIncomplete(campaign.campaignBrief)

  const handleUpdate = () => {
    window.location.reload()
  }

  const brief = campaign.campaignBrief
  const longDate = (d?: string) =>
    d
      ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—'
  const facts = [
    { label: 'Campaign', value: campaign.name || '—' },
    {
      label: 'Dates',
      value:
        campaign.startDate || campaign.endDate
          ? `${longDate(campaign.startDate)} – ${longDate(campaign.endDate)}`
          : '—',
    },
    {
      label: 'Budget',
      value: campaign.budget ? `$${Math.round(campaign.budget).toLocaleString('en-US')}` : '—',
    },
    { label: 'Platforms', value: campaign.socialPlatform?.join(', ') || '—' },
  ]
  const audience = [brief?.targetAgeRange, brief?.targetGender, brief?.targetLocation].filter(
    Boolean,
  ) as string[]

  const Chip: React.FC<{ children: React.ReactNode; lime?: boolean }> = ({ children, lime }) => (
    <span
      className={
        lime
          ? 'rounded-full bg-[#ECFCCB] px-3 py-1 text-xs font-semibold text-[#3F6212]'
          : 'rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-foreground'
      }
    >
      {children}
    </span>
  )
  const Field: React.FC<{ label: string; children: React.ReactNode; wide?: boolean }> = ({
    label,
    children,
    wide,
  }) => (
    <div className={wide ? 'min-w-0 md:col-span-2' : 'min-w-0'}>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="text-sm leading-relaxed text-foreground">{children}</div>
    </div>
  )

  return (
    <>
      <div className="space-y-4">
        {briefNeedsAttention && (
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#FCD9A8] bg-[#FFF4E5] px-4 py-3">
            <AlertCircle className="h-5 w-5 shrink-0 text-[#B45309]" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#92400E]">
                Your brief is missing key details
              </p>
              <p className="text-xs text-[#B45309]">
                Add your key message, objectives and creative direction so athletes know what to
                make.
              </p>
            </div>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#FCD9A8] bg-[#FFFFFF] px-3 py-1.5 text-sm font-semibold text-[#92400E] hover:bg-[#FFFBF5]"
            >
              <Pencil className="h-3.5 w-3.5" /> Complete brief
            </button>
          </div>
        )}

        <section className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-foreground">Campaign basics</h2>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-sm font-semibold text-foreground hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          </div>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {facts.map((f) => (
              <div key={f.label} className="min-w-0 rounded-xl bg-background px-4 py-3">
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {f.label}
                </dt>
                <dd className="mt-1 font-semibold text-foreground">{f.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
          <h2 className="mb-4 text-lg font-bold text-foreground">Creative brief</h2>
          {brief ? (
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
              {brief.description ? (
                <Field label="Overview" wide>
                  {brief.description}
                </Field>
              ) : null}
              {brief.objectives?.length ? (
                <Field label="Objectives">
                  <div className="flex flex-wrap gap-2">
                    {brief.objectives.map((o) => (
                      <Chip key={o} lime>
                        {o}
                      </Chip>
                    ))}
                  </div>
                </Field>
              ) : null}
              {brief.tones?.length ? (
                <Field label="Tone">
                  <div className="flex flex-wrap gap-2">
                    {brief.tones.map((t) => (
                      <Chip key={t}>{t}</Chip>
                    ))}
                  </div>
                </Field>
              ) : null}
              {audience.length || brief.targetAudienceDescription ? (
                <Field label="Target audience" wide>
                  {audience.length ? (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {audience.map((a) => (
                        <Chip key={a}>{a}</Chip>
                      ))}
                    </div>
                  ) : null}
                  {brief.targetAudienceDescription}
                </Field>
              ) : null}
              {brief.primaryMessage || brief.supportingPoints?.length || brief.cta ? (
                <Field label="Key message" wide>
                  {brief.primaryMessage ? <p>{brief.primaryMessage}</p> : null}
                  {brief.supportingPoints?.length ? (
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {brief.supportingPoints.map((pt, i) => (
                        <li key={i}>{pt}</li>
                      ))}
                    </ul>
                  ) : null}
                  {brief.cta ? (
                    <p className="mt-2">
                      <span className="font-semibold">Call to action:</span> {brief.cta}
                    </p>
                  ) : null}
                </Field>
              ) : null}
              {brief.creativeDirection ? (
                <Field label="Creative direction" wide>
                  {brief.creativeDirection}
                </Field>
              ) : null}
              {brief.additionalNotes ? (
                <Field label="Additional notes" wide>
                  {brief.additionalNotes}
                </Field>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No brief added yet.</p>
          )}
        </section>
      </div>

      <EditCampaignModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        campaign={campaign}
        onUpdate={handleUpdate}
      />
    </>
  )
}
